import { useCallback, useEffect, useRef, useState } from "react";
import { EmbeddedConfig } from "../../types/EmbeddedSignupType";

// declare global {
//     interface Window {
//         fbAsyncInit?: () => void;
//         FB?: any;
//     }
// }

export interface SignupResult {
    code: string;
    wabaId: string | null;
    phoneNumberId: string | null;
}

const APP_ID = "605083631903295";
const CONFIG_ID = "603828229379398";

const FB_ORIGINS = ["https://www.facebook.com", "https://web.facebook.com"];
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Loads the Facebook SDK with the appId from the backend and runs Embedded Signup. */
export function useFacebookSignup(config: EmbeddedConfig | null) {
    const [sdkReady, setSdkReady] = useState(false);
    const session = useRef<{ wabaId?: string; phoneNumberId?: string }>({});

    useEffect(() => {
        if (!config) return;
        const init = () => {
            window.FB.init({
                appId: config.appId,
                // appId: APP_ID,
                autoLogAppEvents: true,
                xfbml: false,
                version: "v21.0",
            });
            setSdkReady(true);
        };
        if (window.FB) return init();

        window.fbAsyncInit = init; // must be set before the script loads
        if (!document.getElementById("fb-sdk")) {
            const s = document.createElement("script");
            s.id = "fb-sdk";
            s.src = "https://connect.facebook.net/en_US/sdk.js";
            s.async = true;
            s.defer = true;
            s.crossOrigin = "anonymous";
            document.body.appendChild(s);
        }
    }, [config]);

    useEffect(() => {
        const onMessage = (e: MessageEvent) => {
            if (!FB_ORIGINS.includes(e.origin)) return;
            try {
                const d = JSON.parse(e.data);
                if (d.type === "WA_EMBEDDED_SIGNUP" && d.event === "FINISH") {
                    session.current = {
                        wabaId: d.data?.waba_id,
                        phoneNumberId: d.data?.phone_number_id,
                    };
                }
            } catch {
                /* non-JSON messages are ignored */
            }
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, []);

    const launch = useCallback(
        (): Promise<SignupResult> =>
            new Promise((resolve, reject) => {
                if (!config || !sdkReady || !window.FB) {
                    return reject(new Error("Facebook is still loading. Try again in a moment."));
                }
                const local = ["localhost", "127.0.0.1"].includes(window.location.hostname);
                if (!local && window.location.protocol !== "https:") {
                    return reject(new Error("Facebook login needs HTTPS."));
                }
                session.current = {};
                window.FB.login(
                    (res: any) => {
                        if (!res?.authResponse?.code) {
                            return reject(new Error("Login cancelled."));
                        }
                        // the FINISH message can land just after the login callback
                        (async () => {
                            for (let i = 0; i < 15 && !session.current.wabaId; i++) await sleep(200);
                            resolve({
                                code: res.authResponse.code,
                                wabaId: session.current.wabaId ?? null,
                                phoneNumberId: session.current.phoneNumberId ?? null,
                            });
                        })();
                    },
                    {
                        config_id: config.configId,
                        // config_id: CONFIG_ID,
                        response_type: "code",
                        override_default_response_type: true,
                        extras: { version: "v3" },
                    }
                );
            }),
        [config, sdkReady]
    );

    return { sdkReady, launch };
}